/**
 * ADO TASK API の呼び出し。
 *
 * 認証は Cloudflare の Edge Auth が cookie で担うため、`credentials: 'include'` を必ず付ける。
 * 本番は別ホスト（`api-ado-task.air-closet.ai`）なので、cookie を送るにはこの指定が要る。
 *
 * ローカル開発では Edge Auth が前段にいないので、API をローカルで起こして Vite の
 * proxy（`/api`）経由で叩く（起こし方は docs/web/ado-task.md）。起こしていないときは
 * 画面に読み込み失敗が出る（黙ってサンプルに切り替えると、繋がっていないことに
 * 気づけないため）。
 *
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business ADO TASK API を呼び、タスクと月次経理の型を読み書きする。
 * @graph-connects ado-task-api [calls] タスクと月次経理の型の API
 */
import { logEvent } from '@cortex/faro';
import type { MonthlyFmtTask } from '@cortex/ado-task-fmt';
import type { Task, TeamId } from '@/tasks/task';

/**
 * API の場所。
 *
 * 開発時は Vite の proxy を通す。本番は Pages と別ホストなので絶対 URL で叩く。
 * @graph-connects none
 */
export const API_BASE_URL = import.meta.env.DEV ? '/api' : 'https://api-ado-task.air-closet.ai';

/** API が返すタスク。画面の `Task` とは所属チームの名前だけが違う */
interface ApiTask {
  id: string;
  teamId: TeamId;
  kind: string;
  title: string;
  assignee: string;
  dueDate: string;
  startTime?: string;
  endTime?: string;
  status: Task['status'];
  estimatedHours?: number;
  source?: Task['source'];
  targetMonth?: string;
  note?: string;
  manual?: string;
  sourceRef?: string;
}

/** API が返す月次経理の型 */
interface ApiMonthlyFmtItem {
  id: string;
  business: string;
  majorCategory: string;
  middleCategory: string;
  title: string;
  dueRule: string;
  estimatedHours?: number;
  assignee: string;
  manual?: string;
  isActive: boolean;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business API の応答を画面のタスクに直す。`teamId` を画面の `team` に合わせる
 * @graph-connects none
 */
export function toTask(api: ApiTask): Task {
  return {
    id: api.id,
    team: api.teamId,
    kind: api.kind,
    title: api.title,
    assignee: api.assignee,
    dueDate: api.dueDate,
    startTime: api.startTime,
    endTime: api.endTime,
    status: api.status,
    estimatedHours: api.estimatedHours,
    source: api.source,
    targetMonth: api.targetMonth,
    note: api.note,
    manual: api.manual,
    sourceRef: api.sourceRef,
  };
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 画面のタスクを API の形に直す。値の無い項目は送らない
 * @graph-connects none
 */
export function toApiTask(task: Omit<Task, 'id'> & { id?: string }): Record<string, unknown> {
  return {
    id: task.id,
    teamId: task.team,
    kind: task.kind,
    title: task.title,
    assignee: task.assignee,
    dueDate: task.dueDate,
    startTime: task.startTime,
    endTime: task.endTime,
    status: task.status,
    estimatedHours: task.estimatedHours ?? undefined,
    source: task.source ?? 'manual',
    targetMonth: task.targetMonth,
    note: task.note,
    manual: task.manual,
    sourceRef: task.sourceRef,
  };
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 失敗の応答から理由を読む。JSON でなければ null を返し、呼び出し側が状態番号で伝える
 * @graph-connects none
 */
async function readErrorBody(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch (error: unknown) {
    logEvent('api_error_body_unreadable', {
      status: String(response.status),
      reason: error instanceof Error ? error.message : String(error),
    });
    return null;
  }
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business API を呼び、失敗したら理由が分かる形で投げる。認証切れは読み込み直しを促す文言にする
 * @graph-connects ado-task-api [calls] ADO TASK API
 */
export async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // Headers 型は配列でも渡せるためスプレッドは使わず、既定の Content-Type を先に入れてから上書きする
  const headers = new Headers({ 'Content-Type': 'application/json' });
  for (const [key, value] of new Headers(init?.headers).entries()) {
    headers.set(key, value);
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    credentials: 'include',
    headers,
  });

  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      throw new Error('認証が切れているようです。画面を読み込み直してください');
    }
    // 応答が JSON とは限らない（502 の HTML など）。読めなければ状態番号で伝える
    const body = await readErrorBody(response);
    const message =
      typeof body === 'object' && body !== null && 'error' in body
        ? String((body as { error: unknown }).error)
        : `通信に失敗しました（${String(response.status)}）`;
    throw new Error(message);
  }

  return (await response.json()) as T;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business チームと期間でタスクを引く
 * @graph-connects request [calls] API を呼ぶ
 * @graph-connects toTask [calls] 応答を画面のタスクに直す
 */
export async function fetchTasks(team: TeamId, from?: string, to?: string): Promise<Task[]> {
  const params = new URLSearchParams({ team });
  if (from !== undefined) {
    params.set('from', from);
  }
  if (to !== undefined) {
    params.set('to', to);
  }
  const body = await request<{ tasks: ApiTask[] }>(`/tasks?${params.toString()}`);
  return body.tasks.map(toTask);
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business タスクを 1 件作る
 * @graph-connects request [calls] API を呼ぶ
 * @graph-connects toApiTask [calls] 送る形に直す
 */
export async function createTask(task: Omit<Task, 'id'> & { id?: string }): Promise<Task> {
  const body = await request<{ task: ApiTask }>('/tasks', {
    method: 'POST',
    body: JSON.stringify(toApiTask(task)),
  });
  return toTask(body.task);
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 更新の中身を、消したい項目が伝わる形にする。`JSON.stringify` は値が undefined のキーを丸ごと落とすため、そのままでは「外した」ことがサーバーへ届かない。渡された項目だけを null にして送る
 * @graph-connects none
 */
export function toPatchBody(patch: Partial<Omit<Task, 'id'>>): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(patch)) {
    // 渡された項目だけを送る。team だけは API 側の名前が teamId
    body[key === 'team' ? 'teamId' : key] = value === undefined ? null : value;
  }
  return body;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business タスクを直す。送るのは変える項目だけ。外した項目は null で送り、保存先からも消す
 * @graph-connects request [calls] API を呼ぶ
 * @graph-connects toPatchBody [calls] 消したい項目が伝わる形にする
 */
export async function updateTask(id: string, patch: Partial<Omit<Task, 'id'>>): Promise<Task> {
  const body = await request<{ task: ApiTask }>(`/tasks/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(toPatchBody(patch)),
  });
  return toTask(body.task);
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business タスクを消す
 * @graph-connects request [calls] API を呼ぶ
 */
export async function deleteTask(id: string): Promise<void> {
  await request<{ id: string }>(`/tasks/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business FMT の型（月次経理・決算）を全件引く（並べ替えは画面）
 * @graph-connects request [calls] API を呼ぶ
 */
export async function fetchMonthlyFmtItems(
  // 決算タスクFMT なら `/closing-fmt-items`。列は同じなので読み方は変わらない
  basePath = '/monthly-fmt-items',
): Promise<MonthlyFmtTask[]> {
  const body = await request<{ items: ApiMonthlyFmtItem[] }>(basePath);
  return body.items.map((item) => ({
    id: item.id,
    business: item.business,
    majorCategory: item.majorCategory,
    middleCategory: item.middleCategory,
    title: item.title,
    dueRule: item.dueRule,
    estimatedHours: item.estimatedHours ?? null,
    assignee: item.assignee,
    manual: item.manual,
  }));
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 月次経理の型を 1 件作る
 * @graph-connects request [calls] API を呼ぶ
 */
export async function createMonthlyFmtItem(
  item: MonthlyFmtTask,
  basePath = '/monthly-fmt-items',
): Promise<MonthlyFmtTask> {
  const body = await request<{ item: ApiMonthlyFmtItem }>(basePath, {
    method: 'POST',
    body: JSON.stringify({
      id: item.id === '' ? undefined : item.id,
      business: item.business,
      majorCategory: item.majorCategory,
      middleCategory: item.middleCategory,
      title: item.title,
      dueRule: item.dueRule,
      estimatedHours: item.estimatedHours ?? undefined,
      assignee: item.assignee,
      manual: item.manual,
      isActive: true,
    }),
  });
  return { ...item, id: body.item.id };
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 月次経理の型を直す
 * @graph-connects request [calls] API を呼ぶ
 */
export async function updateMonthlyFmtItem(
  item: MonthlyFmtTask,
  basePath = '/monthly-fmt-items',
): Promise<void> {
  await request<{ item: ApiMonthlyFmtItem }>(`${basePath}/${encodeURIComponent(item.id)}`, {
    method: 'PATCH',
    body: JSON.stringify({
      business: item.business,
      majorCategory: item.majorCategory,
      middleCategory: item.middleCategory,
      title: item.title,
      dueRule: item.dueRule,
      estimatedHours: item.estimatedHours,
      assignee: item.assignee,
      // 空にして消せるよう、書いていないときは null を送る
      manual: item.manual ?? null,
    }),
  });
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 月次経理の型を消す
 * @graph-connects request [calls] API を呼ぶ
 */
export async function deleteMonthlyFmtItem(
  id: string,
  basePath = '/monthly-fmt-items',
): Promise<void> {
  await request<{ id: string }>(`${basePath}/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

/** カレンダー取り込みの結果 */
export interface CalendarImportResult {
  /** 新しくタスクにした予定の数 */
  imported: number;
  /** 動いていたので日付・時刻・内容を直した予定の数 */
  updated: number;
  /** カレンダーから消えたので消した（未着手の）タスクの数 */
  removed: number;
  /** 取り込み済みで変わりの無かった予定の数 */
  skipped: number;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business ログインしている本人の Google カレンダーの予定を、期間と担当を指定してタスクへ取り込む
 * @graph-connects request [calls] API を呼ぶ
 */
export async function importCalendar(
  team: TeamId,
  from: string,
  to: string,
  assignee: string,
): Promise<CalendarImportResult> {
  return request<CalendarImportResult>('/calendar-import', {
    method: 'POST',
    body: JSON.stringify({ team, from, to, assignee }),
  });
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business カレンダーを共有する先（ADO TASK API の SA のメールアドレス）と、ログインしている人に結びついた担当（メンバー設定のメールアドレスで引く。無ければ null）を引く
 * @graph-connects request [calls] API を呼ぶ
 */
export async function fetchCalendarImportInfo(
  team: TeamId,
): Promise<{ shareWith: string; assignee: string | null }> {
  const body = await request<{ shareWith: string; assignee?: string | null }>(
    `/calendar-import?team=${encodeURIComponent(team)}`,
  );
  return { shareWith: body.shareWith, assignee: body.assignee ?? null };
}

/** 設定（メンバー・種別）の 1 件。色を持つのは種別だけ */
export interface SettingEntry {
  /** 一意な ID */
  id: string;
  /** 所属チーム */
  team: TeamId;
  /** 表示名 */
  name: string;
  /** 並び順 */
  order: number;
  /** 表示色（種別のみ） */
  color?: string;
  /** Google アカウントのメールアドレス（メンバーのみ）。カレンダー取り込みで本人を担当に結びつける */
  email?: string;
}

/** API が返す設定 */
interface ApiSetting {
  id: string;
  teamId: TeamId;
  name: string;
  order: number;
  color?: string;
  email?: string;
}

/** @graph-connects none */
function toSettingEntry(setting: ApiSetting): SettingEntry {
  const { teamId, ...rest } = setting;
  return { ...rest, team: teamId };
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business メンバーまたは種別の設定を並び順で引く
 * @graph-connects request [calls] API を呼ぶ
 */
export async function fetchSettings(path: 'members' | 'kind-settings'): Promise<SettingEntry[]> {
  const body = await request<{ settings: ApiSetting[] }>(`/${path}`);
  return body.settings.map(toSettingEntry);
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 設定を 1 件足す
 * @graph-connects request [calls] API を呼ぶ
 */
export async function createSetting(
  path: 'members' | 'kind-settings',
  entry: Omit<SettingEntry, 'id'> & { id?: string },
): Promise<SettingEntry> {
  const body = await request<{ setting: ApiSetting }>(`/${path}`, {
    method: 'POST',
    body: JSON.stringify({
      id: entry.id,
      teamId: entry.team,
      name: entry.name,
      order: entry.order,
      color: entry.color,
      email: entry.email,
    }),
  });
  return toSettingEntry(body.setting);
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 設定を 1 件直す。渡した項目だけを送る
 * @graph-connects request [calls] API を呼ぶ
 */
export async function updateSetting(
  path: 'members' | 'kind-settings',
  id: string,
  patch: { name?: string; order?: number; color?: string; email?: string },
): Promise<void> {
  await request<{ setting: ApiSetting }>(`/${path}/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 設定を 1 件消す
 * @graph-connects request [calls] API を呼ぶ
 */
export async function deleteSetting(path: 'members' | 'kind-settings', id: string): Promise<void> {
  await request<null>(`/${path}/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
